import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import Auth0ProviderWithHistory from './providers/Auth0ProviderWithHistory';
import { apiService } from './services/api';
import { ProtectedRoute } from './components/auth';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard/Dashboard';

// Main Page Routers
import ClientsRouter from './pages/Clients';
import { ManageCoaches, PerformanceAnalytics, CoachDetails } from './pages/Coaches';

// Libraries Pages
import { 
  ExerciseLibrary, 
  EquipmentLibrary, 
  AdaptationsLibrary, 
  MuscleGroupsLibrary, 
  LibraryStatistics 
} from './pages/Libraries';

// Business Analytics Pages
import { ClientAnalytics, RevenueDashboard, PerformanceReports } from './pages/BusinessAnalytics';

// System Settings Pages
import UserManagement from './pages/SystemSettings/UserManagement';
import SystemConfiguration from './pages/SystemSettings/SystemConfiguration';
import DataExportImport from './pages/SystemSettings/DataExportImport';

import Login from './pages/Login';
import './App.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#1890ff',
    borderRadius: 8,
    colorBgContainer: '#ffffff',
  },
};

// Component to initialize API service with Auth0 token
const ApiInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      // Set up API service with Auth0 token getter
      apiService.setTokenGetter(getAccessTokenSilently);
      console.log('✅ API service initialized with Auth0 tokens');
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={theme}>
        <Router>
          <Auth0ProviderWithHistory>
            <ApiInitializer>
              <div className="App">
                <Routes>
                {/* Public login route */}
                <Route path="/login" element={<Login />} />
                
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  {/* Dashboard */}
                  <Route index element={<Dashboard />} />
                  
                  {/* Clients Routes */}
                  <Route path="clients" element={<ClientsRouter />} />
                  <Route path="clients/onboarding" element={<ClientsRouter />} />
                  
                  {/* Coaches Routes */}
                  <Route path="coaches" element={<ManageCoaches />} />
                  <Route path="coaches/analytics" element={<PerformanceAnalytics />} />
                  <Route path="coaches/:coachId" element={<CoachDetails />} />
                  
                  {/* Libraries Routes */}
                  <Route path="libraries/exercises" element={<ExerciseLibrary />} />
                  <Route path="libraries/equipment" element={<EquipmentLibrary />} />
                  <Route path="libraries/adaptations" element={<AdaptationsLibrary />} />
                  <Route path="libraries/muscle-groups" element={<MuscleGroupsLibrary />} />
                  <Route path="libraries/statistics" element={<LibraryStatistics />} />
                  
                  {/* Business Analytics Routes - Admin Only */}
                  <Route path="analytics/clients" element={
                    <ProtectedRoute requiredRoles={['admin', 'coach']}>
                      <ClientAnalytics />
                    </ProtectedRoute>
                  } />
                  <Route path="analytics/revenue" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <RevenueDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="analytics/reports" element={
                    <ProtectedRoute requiredRoles={['admin', 'coach']}>
                      <PerformanceReports />
                    </ProtectedRoute>
                  } />
                  
                  {/* System Settings Routes - Admin Only */}
                  <Route path="settings/users" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <UserManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/configuration" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <SystemConfiguration />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/data" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <DataExportImport />
                    </ProtectedRoute>
                  } />
                </Route>
                </Routes>
              </div>
            </ApiInitializer>
          </Auth0ProviderWithHistory>
        </Router>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
